import { SocialLinks } from '../components/SocialLinks';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, GraduationCap, Landmark } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import './HomeElevated.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
  THLP Home — React port of the Fable-elevated Claude Design ("THLP Home.dc.html").
  Styles live in HomeElevated.css, scoped under .thlp-home so they cannot leak into other pages.
  This page renders its own header (nav) + footer per the design; App.js hides the global
  Navbar/Footer on the "/" route to avoid duplication.
*/

const REDCARDS = [
  { grad: 'linear-gradient(120deg,#b63435,#7f2323)', Icon: <AlertTriangle size={22} strokeWidth={2.2} />, num: <>40<span style={{ fontSize: 20 }}>%</span></>, text: 'Fewer than 4 out of 10 third graders read proficiently in the State of Delaware.' },
  { grad: 'linear-gradient(120deg,#a93030,#701f1f)', Icon: <GraduationCap size={22} strokeWidth={2.2} />, num: <>4X<span style={{ font: "700 19px/1 'Rubik'", color: '#f6c9c9' }}>more likely</span></>, text: 'Students who are not reading proficiently by third grade are 4x more likely to drop out of high school.' },
  { grad: 'linear-gradient(120deg,#7f2323,#511616)', Icon: <Landmark size={22} strokeWidth={2.2} />, num: '$272,000', text: 'Estimated lifetime public cost associated with each student who does not graduate high school.' },
];

const PINS = [
  { n: 1, left: '61.5%', top: '29%', ar: 'entry 2% cover 28%', d: 34, fs: 15, bg: '#c98686', sh: '0 7px 15px rgba(120,50,50,.4)', stem: 14 },
  { n: 2, left: '44%', top: '32.5%', ar: 'entry 5% cover 31%', d: 38, fs: 16, bg: '#bd7373', sh: '0 8px 16px rgba(120,45,45,.42)', stem: 15 },
  { n: 3, left: '61.5%', top: '41.5%', ar: 'entry 8% cover 34%', d: 42, fs: 17, bg: '#a95a5a', sh: '0 9px 18px rgba(110,40,40,.44)', stem: 16 },
  { n: 4, left: '73.5%', top: '50%', ar: 'entry 11% cover 37%', d: 46, fs: 18, bg: '#984848', sh: '0 10px 20px rgba(100,35,35,.46)', stem: 17 },
  { n: 5, left: '53%', top: '56%', ar: 'entry 14% cover 40%', d: 50, fs: 19, bg: '#833838', sh: '0 11px 22px rgba(90,30,30,.48)', stem: 18 },
  { n: 6, left: '40.5%', top: '68%', ar: 'entry 17% cover 43%', d: 55, fs: 21, bg: '#6c2828', sh: '0 13px 26px rgba(80,22,22,.5)', stem: 19 },
  { n: 7, left: '60%', top: '85%', ar: 'entry 20% cover 46%', d: 60, fs: 24, bg: '#4f1717', sh: '0 15px 30px rgba(70,18,18,.54)', stem: 20 },
];

const LEGEND = [
  { n: 1, bg: '#c98686', when: 'By Age 3', text: 'Children miss out on millions of words.' },
  { n: 2, bg: '#bd7373', when: 'By Grade 3', text: 'Not reading proficiently.', badge: '4× more likely to drop out' },
  { n: 3, bg: '#a95a5a', when: 'By Grade 5', text: 'Trouble learning as content grows harder.' },
  { n: 4, bg: '#984848', when: 'By Grade 8', text: 'Start to avoid school.' },
  { n: 5, bg: '#833838', when: 'By Grade 10', text: 'Chronic absenteeism, frequent infractions.' },
  { n: 6, bg: '#6c2828', when: 'By Grade 12', text: 'Drop out of school.' },
  { n: 7, bg: '#4f1717', when: 'Adulthood', text: '$272,000 in taxpayer costs.', cost: true },
];

const SOLUTION = [
  { flip: false, img: '/assets/kids-sharing-books.jpg', alt: 'Two children sharing a book in front of a home bookshelf', h3: 'teal', pill: 'pill-teal', title: 'Curated Books for Every Child', body: 'From birth through eighth grade, each child will receive ten books every year, building a personal home library by adolescence. Books will be distributed through multiple pathways based on age and family circumstances.', tags: ['10 books per year', '200+ books by middle school', 'Diverse abilities'] },
  { flip: true, img: '/assets/sol-medicine.png', alt: 'Parent reading with baby', h3: 'navy', pill: 'pill-navy', title: 'Books Are Medicine and Prevention', body: <>Every Delaware newborn will receive the <em>Welcome to Delaware, Baby!</em> Home Library Set. Families will receive ten developmentally appropriate books each year through trusted healthcare and community partners.</>, tags: ['BIRTH TO 5 YEARS', '100+ BOOKS BY KINDERGARTEN*', 'MULTILINGUAL'] },
  { flip: false, img: '/assets/sol-bookshelf.png', alt: 'Colorful home bookshelf', h3: 'teal', pill: 'pill-teal', title: 'Books Through Early Learning & Schools', body: 'Children enrolled in PreK and early childhood centers will receive books through their program. Beginning in kindergarten, all students will receive books at school twice each year during fall and spring distributions of carefully selected titles.', tags: ['PreK through 8th grade', 'High-quality & high-interest'] },
  { flip: true, img: '/assets/kids-table.png', alt: 'Two children reading together', h3: 'navy', pill: 'pill-navy', title: 'Books That Connect Learning to Life', body: 'Each annual set for K through 8th grade will include four books aligned with Delaware’s educational priorities, alongside six high-interest titles chosen for joy, diversity, engagement, cultural relevance, and career exploration.', tags: ['Financial literacy', 'Diverse heroes & history', 'STEM', 'Social emotional learning'] },
];

const PATHWAYS = [
  { title: 'We Hire With Purpose', desc: 'Creating meaningful jobs in the communities we serve.', svg: <><rect x="2" y="7" width="20" height="14" rx="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></> },
  { title: 'We See Potential', desc: 'Valuing character and commitment over credentials.', svg: <><circle cx="12" cy="8" r="6"></circle><path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11"></path></> },
  { title: 'We Build Skills', desc: 'Paid time for literacy, numeracy, and digital training.', svg: <><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"></path><path d="M9 18h6"></path><path d="M10 22h4"></path></> },
  { title: 'We Grow Together', desc: 'Strengthening families and Delaware’s workforce.', svg: <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.29 1.51 4.04 3 5.5l7 7Z"></path> },
];

// H6: "Annual Impact by 2032" stat band, restored from the old site and rebuilt in the new design system.
const MILESTONES = [
  { num: '150,000', label: 'Kids' },
  { num: '1.5 Million', label: 'Books' },
  { num: '50+', label: 'Employees' },
  { num: '100%', label: 'of Donations to Mission' },
];

export default function Home() {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  useEffect(() => { loadMetrics(); }, []);

  const loadMetrics = async () => {
    try {
      const response = await axios.get(`${API}/metrics`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Failed to load metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterLoading(true);

    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      if (response.data.success) {
        toast.success(response.data.message || 'Thank you for subscribing!');
        setNewsletterEmail('');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <div className="thlp-home" id="top">

      {/* HERO */}
      <header className="hero">
        <img className="hero-img" src="/assets/hero-apartment.png" alt="A child reading at home" />
        <div className="hero-brighten" aria-hidden="true" />
        <div className="hero-scrim" />
        <div className="hero-inner wrap">
          <div className="hero-copy">
            <span className="eyebrow-pill in">TEN BOOKS • EVERY CHILD • EVERY YEAR</span>
            <h1 className="in in-2">Book Ownership<br />Changes Everything</h1>
            <p className="lede in in-3">Imagine growing up without books. No stories to explore. No mirrors to see yourself. No windows to the wider world.</p>
            <p className="lede in in-3">Books are the foundation of literacy and one of the most cost-effective investments we can make in a child&rsquo;s future. We envision a home library for every child.</p>
            <div className="hero-ctas in in-4">
              <Link className="btn btn-gold" to="/donate" data-testid="hero-join-button">Donate Now</Link>
              <Link className="btn btn-ghost" to="/model">See Our Model</Link>
            </div>
          </div>
        </div>
      </header>

      <main>

        {/* IMPACT BAND */}
        <section className="impact">
          <div className="wrap">
            <p className="impact-intro rv">The Home Library Project is building the nation&rsquo;s first statewide initiative to ensure every child in Delaware has a home library from birth through 8th grade.</p>
          </div>
          <div className="wrap impact-grid">
            <div className="stat rv"><b className="t1 num">10</b><span>books per child, every year</span></div>
            <div className="stat rv rv-2"><b className="t2 num">100+</b><span>books by kindergarten</span></div>
            <div className="stat rv rv-2"><b className="t1 num">200+</b><span>books by eighth grade</span></div>
            <div className="stat rv rv-3"><b className="t2 num">&lt;$50</b><span>per child per year at scale</span></div>
          </div>
        </section>

        {/* PROBLEM · COST BANNER */}
        <section className="crisis" id="crisis">
          <div className="crisis-bg" style={{ backgroundImage: "url('/assets/crisis-collage.png')" }} />
          <div className="crisis-tint" />
          <div className="crisis-kicker"><span>Delaware&rsquo;s Literacy Crisis Is Real</span></div>
          <div className="crisis-body wrap">
            <div className="crisis-grid">
              <div className="rv">
                <h2 className="h2">The Cost of Growing Up<br />Without Books</h2>
                <p className="lede">Delaware is facing a literacy crisis. More than 60% of children from low-income households have no books at home, setting the stage for persistent gaps in literacy, and contributing to approximately 1,000 Delaware students dropping out of school each year. Low literacy is not just an educational challenge. It is a long-term economic and community issue.</p>
              </div>
              <div className="red-stack">
                {REDCARDS.map((c, i) => (
                  <div key={i} className={`red-card rv${i ? ` rv-${i + 1}` : ''}`} style={{ background: c.grad }}>
                    <span className="rc-badge">{c.Icon}</span>
                    <div className="inner"><b className="num">{c.num}</b><p>{c.text}</p></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* PROBLEM · ROADMAP */}
        <section className="roadmap">
          <div className="wrap">
            <div className="roadmap-head rv">
              <span className="eyebrow" style={{ letterSpacing: '.28em' }}>Prevention Costs Pennies</span>
              <h2 className="h2">Failing Our Children Costs Millions</h2>
              <p className="lede">Every year Delaware fails to address childhood literacy, we sign a promissory note worth approximately <strong style={{ color: 'var(--navy)' }}>$272 million</strong> in long-term public costs.</p>
              <p className="punch">That&rsquo;s nearly <strong style={{ color: 'var(--brick)', textDecoration: 'underline' }}>$3 billion every decade.</strong></p>
            </div>

            <div className="road-stage">
              <div className="road-square" aria-hidden="true">
                <img src="/assets/journey.png" alt="" />
                {PINS.map((p) => (
                  <div key={p.n} className="pin" data-pin={p.n} style={{ left: p.left, top: p.top, animationRange: p.ar }}>
                    <span className="dot" style={{ width: p.d, height: p.d, fontSize: p.fs, background: p.bg, boxShadow: p.sh }}>{p.n}</span>
                    <span className="stem" style={{ height: p.stem, background: p.bg }} />
                    <span className="ground" />
                  </div>
                ))}
              </div>

              <div className="road-legend">
                <div className="intro">When we fail to intervene early, the consequences follow a predictable and costly path:</div>
                <div>
                  {LEGEND.map((it) => (
                    <div key={it.n} className="leg-row" data-leg={it.n}>
                      <span className="chip" style={{ background: it.bg }}>{it.n}</span>
                      <div>
                        <b>{it.when}</b>
                        <p className={it.cost ? 'cost' : undefined}>{it.text}</p>
                        {it.badge && <span className="leg-badge">{it.badge}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="road-callout rv">
                <b>We will never catch up by chasing the problem downstream.</b>
                <p>Early literacy prevention breaks the cycle: books in homes from birth, before the gaps open.</p>
              </div>
            </div>
          </div>
        </section>

        {/* SOLUTION */}
        <section className="solution" id="solution">
          <div className="wrap">
            <div className="sec-head rv">
              <span className="eyebrow-pill" style={{ padding: '7px 16px', fontSize: '11.5px' }}>The Solution</span>
              <h2 className="h2">How We Help Kids Become Readers</h2>
              <p className="lede"><strong style={{ color: 'var(--navy)' }}>Our vision:</strong> a scalable, evidence-based preventive approach designed to stop literacy gaps before they begin.</p>
            </div>
            <div className="sol-stack">
              {SOLUTION.map((s, i) => (
                <article key={i} className={`sol-card rv${s.flip ? ' flip' : ''}`}>
                  <div className="sol-media"><img src={s.img} alt={s.alt} /></div>
                  <div className="sol-body">
                    <h3 className={s.h3}>{s.title}</h3>
                    <p>{s.body}</p>
                    <div className="pillrow">{s.tags.map((t, j) => <span key={j} className={`pill ${s.pill}`}>{t}</span>)}</div>
                  </div>
                </article>
              ))}
            </div>
            <p className="footnote">*Delaware made a smart investment in Dolly Parton&rsquo;s Imagination Library. The evidence is clear: children need more books, not fewer. Together, DPIL and The Home Library Project help children reach 100 books by kindergarten and 200 or more by the end of middle school.</p>
          </div>
        </section>

        {/* PATHWAYS */}
        <section className="pathways">
          <img className="pathways-bg" src="/bg-workforce.jpg" alt="" />
          <div className="pathways-tint" />
          <div className="pathways-inner">
            <div className="rv">
              <span className="eyebrow-pill" style={{ padding: '7px 16px', fontSize: '11.5px' }}>Pathways to Purpose</span>
              <h2 className="h2">A Stronger Workforce Starts Here</h2>
              <p className="sub">Children gaining access to books. Adults gaining access to opportunity.</p>
              <p className="body">Literacy is the first workforce skill. The Home Library Project strengthens Delaware&rsquo;s future by investing in both children and adults. Every child deserves books to build a lifetime of learning, and every adult deserves the opportunity to grow, contribute, and build a brighter future. Together, we are creating pathways to purpose that strengthen families, communities, and Delaware.</p>
            </div>
            <div className="value-grid">
              {PATHWAYS.map((p, i) => (
                <div key={i} className={`value-tile rv${i ? ` rv-${Math.min(i + 1, 3)}` : ''}`}>
                  <span className="icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7fd0d9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{p.svg}</svg></span>
                  <b>{p.title}</b>
                  <p>{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* MILESTONE / ANNUAL IMPACT BY 2032 */}
        <section className="milestone" id="milestone">
          <div className="wrap">
            <div className="milestone-head rv">
              <span className="milestone-rule" aria-hidden="true" />
              <h2 className="milestone-title">Annual Impact by 2032</h2>
            </div>
            <div className="milestone-grid">
              {MILESTONES.map((m, i) => (
                <div key={i} className={`milestone-tile rv${i ? ` rv-${Math.min(i + 1, 3)}` : ''}`}>
                  <b className="num">{m.num}</b>
                  <span>{m.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FOUNDER */}
        <section className="founder" id="story">
          <img src="/assets/laurie.png" alt="Founder Laurie Girardi" />
          <div className="founder-body rv">
            <span className="eyebrow-pill">Our Founding Story</span>
            <h2 className="h2">Passion With a Purpose</h2>
            <div className="founder-mark" aria-hidden="true">&ldquo;</div>
            <blockquote>&ldquo;To tackle literacy at its root, we must put books directly into children&rsquo;s hands consistently, predictably, and early enough to matter. Access is important. Ownership is the difference-maker.&rdquo;</blockquote>
            <div className="founder-attr">Laurie Girardi<span> &middot; Founder &amp; CEO</span></div>
            <Link className="founder-link" to="/about">Read the full story &rarr;</Link>
          </div>
        </section>

        {/* CTA BAND */}
        <section className="cta" id="donate">
          <img className="cta-bg" src="/assets/hero-steps.png" alt="" />
          <div className="cta-tint" />
          <div className="cta-inner wrap">
            <div className="rv">
              <h2>Together, We Can Build Home<br />Libraries for Every Child.</h2>
              <p className="cta-lead"><strong>Ten books per year. Every child. Every year.</strong> Less than $50 per child per year at scale. Measurable, lasting impact.</p>
            </div>
            <div className="cta-actions">
              <Link className="btn btn-gold" to="/donate" data-testid="cta-join-button">Donate Now</Link>
              <span className="cta-chevrons"><svg width="64" height="27" viewBox="0 0 76 30" fill="none"><path d="M0 3 L9 3 L21 15 L9 27 L0 27 L12 15 Z" fill="#fad53a" opacity="0.4"></path><path d="M23 3 L32 3 L44 15 L32 27 L23 27 L35 15 Z" fill="#fad53a" opacity="0.7"></path><path d="M46 3 L55 3 L67 15 L55 27 L46 27 L58 15 Z" fill="#fad53a" opacity="1"></path></svg></span>
            </div>
          </div>
        </section>

        {/* NEWSLETTER / JOIN THE MOVEMENT */}
        <section className="newsletter" id="newsletter">
          <div className="wrap newsletter-inner rv">
            <span className="eyebrow-pill">JOIN THE MOVEMENT</span>
            <h2 className="h2">Don&rsquo;t Miss a Single Story</h2>
            <p className="newsletter-body">We share updates from the children whose lives are changing, the schools joining the movement, and the community making it all possible.</p>
            <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                className="newsletter-input"
                placeholder="Your email address"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                aria-label="Your email address"
              />
              <button type="submit" className="btn btn-teal" disabled={newsletterLoading}>
                {newsletterLoading ? 'Subscribing...' : 'Count Me In'}
              </button>
            </form>
            <p className="newsletter-fine">You&rsquo;ll get: Impact stories &middot; Behind-the-scenes updates &middot; Event invitations</p>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="footer" id="footer">
        <div className="wrap footer-inner">
          <Link to="/"><img className="footer-logo" src="/assets/logo-white.png" alt="The Home Library Project" /></Link>
          <div className="footer-right">
            <nav className="footer-links" aria-label="Footer">
              <Link to="/">Home</Link>
              <Link to="/about">About</Link>
              <Link to="/research">Research</Link>
              <Link to="/contact">Contact</Link>
              <Link className="gold" to="/donate">Donate</Link>
            </nav>
            <SocialLinks />
            <div className="footer-meta">
              &copy; 2026 The Home Library Project. 501(c)(3) Nonprofit Organization.<br />
              <Link to="/privacy">Privacy Policy</Link> &middot; <Link to="/terms">Terms of Service</Link>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}
